/**
 * resourceMap 变更通知
 * 当 presentation 上的 resourceMap 发生变更时，通知使用到 resourceMap 的 model 进行变更
 * ResourceMapManager 可以精确查找到需要更新的 model，避免进行无意义的通知/UI转换
 */
import { InheritPropsTypes } from '../utils/on-props-change';
import { PresentationEventTypes } from './event-manager/event-types';
import type { DataModel } from '../model/data-model';
import type { IEventManager } from './event-manager';

export class ResourceMapManager {
    // relId -> models，当 resourceMap 某个 relId 变化时，通知对应的 models
    private observers = new Map<string, Set<DataModel>>();
    // 已被删除的 model，被删除的 model 不需要再进行变更通知
    private detachedModels = new WeakSet<DataModel>();

    /**
     * 初始化阶段注册三种事件监听
     * 1. 当有 model 读取 resourceMap 时：此 model 需要进行变更通知
     * 2. 当 resourceMap 发生改变时：统计需要变更的 model，并进行通知
     * 3. 当 model 销毁时：此 model 不再需要通知
     *
     * TODO: 如果在 model 直接变更了 relId，旧的 relId 监听并未删除，会有冗余
     */
    public constructor(private events: IEventManager) {
        this.events.on(PresentationEventTypes.ModelObserveResourceMap, (model, relId) =>
            this.addObserver(relId, model as DataModel),
        );
        this.events.on(PresentationEventTypes.ResourceMapChange, (relId, descriptor) =>
            this.notifyResourceChange(relId, descriptor),
        );
        this.events.on(PresentationEventTypes.DetachDataModel, (model) => this.detachedModels.add(model as DataModel));
        this.events.on(PresentationEventTypes.AttachDataModel, (model) =>
            this.detachedModels.delete(model as DataModel),
        );
    }

    /**
     * 为读取了 resourceMap 的 model 添加监听，以便在 resourceMap 更新时进行通知
     */
    private addObserver(relId: string, model: DataModel) {
        const models = this.observers.get(relId);
        if (!models) {
            this.observers.set(relId, new Set([model]));
            return;
        }
        models.add(model);
    }

    /**
     * resourceMap 更新时进行通知
     */
    private notifyResourceChange(relId: string, descriptor: { src?: string }) {
        // 非 src 的变更目前不需要通知
        if (!descriptor.src) {
            return;
        }
        const models = this.observers.get(relId);
        if (!models) {
            return;
        }
        for (const model of models) {
            if (!this.detachedModels.has(model)) {
                model.store.onPropsChange([InheritPropsTypes.Resources]);
            }
        }
        this.observers.delete(relId);
    }
}
